import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import { API } from '../lib/api';



const PushNotificationPrompt = ({ user }) => {
  const { session } = useAuth();
  const [publicKey, setPublicKey] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    if (process.env.NODE_ENV !== 'production') return;
    const capabilities = await axios.get(`${API}/capabilities`).catch(() => null);
    if (!capabilities?.data.push || !capabilities.data.push_public_key) return;
    setPublicKey(capabilities.data.push_public_key);
    // Check if notifications are supported
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    // Check if user has already decided
    const dismissed = localStorage.getItem('push-notification-dismissed');
    if (dismissed) {
      return;
    }

    // Check current permission
    if (Notification.permission === 'default') {
      // Show prompt after a delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000); // Show after 5 seconds
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToNotifications = async () => {
    setSubscribing(true);

    try {
      // Request permission
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        setShowPrompt(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await axios.post(
        `${API}/notifications/subscribe`,
        {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        },
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );

      toast.success('Notifications enabled! 🔔');
      setShowPrompt(false);
      localStorage.setItem('push-notification-enabled', 'true');
    } catch (error) {
      console.error('Notification subscription error:', error);
      toast.error('Failed to enable notifications');
    } finally {
      setSubscribing(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('push-notification-dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm" data-testid="push-notification-prompt">
      <Card className="glass-strong p-4 shadow-2xl border-primary/30 animate-in slide-in-from-bottom-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold mb-1">Enable Notifications?</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Get notified when your requests are accepted, coming up, or playing!
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90"
                onClick={subscribeToNotifications}
                disabled={subscribing}
                data-testid="enable-notifications-button"
              >
                {subscribing ? 'Enabling...' : 'Enable'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                data-testid="dismiss-notifications-button"
              >
                Not Now
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={handleDismiss}
            data-testid="close-notification-prompt"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PushNotificationPrompt;
